import React, { useEffect, useState } from "react";
import { Mail, MessageCircle, Trash2, Phone, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS_COLORS = {
  new: "bg-[#D4AF37]/15 text-[#0A1128] border-[#D4AF37]",
  read: "bg-blue-50 text-blue-700 border-blue-200",
  replied: "bg-green-50 text-green-700 border-green-200",
  closed: "bg-slate-100 text-slate-500 border-slate-200",
};

const AdminCatalogueRequests = () => {
  const [items, setItems] = useState([]);

  const load = () => api.get("/catalogue-requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.put(`/catalogue-requests/${id}`, { status });
    toast.success("Updated");
    load();
  };

  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/catalogue-requests/${id}`);
    load();
  };

  return (
    <div data-testid="admin-catalogue-requests">
      <h1 className="font-display text-3xl text-[#0A1128] mb-2">Catalogue Download Leads</h1>
      <p className="text-slate-500 text-sm mb-8">Every visitor who requested a catalogue download.</p>
      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-500">
          No catalogue download requests yet.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => {
            const fullPhone = `${r.phone_country_code}${r.phone_number}`;
            const cleanPhone = fullPhone.replace(/\D/g, "");
            return (
              <div key={r.id} className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`catreq-${r.id}`}>
                <div className="flex flex-wrap justify-between items-start gap-4 mb-3">
                  <div>
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="font-display text-xl text-[#0A1128]">{r.name}</h3>
                      <span className={`text-[10px] px-2 py-1 rounded-sm uppercase tracking-[0.15em] border ${STATUS_COLORS[r.status] || ""}`}>{r.status}</span>
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-1 rounded-sm">
                        <FileText size={10} /> {r.catalogue_title_en || "Catalogue"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{r.company} • {r.country} • {new Date(r.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={`mailto:${r.email}`} title={r.email} className="p-2 border border-slate-200 hover:border-[#D4AF37] rounded-sm text-slate-600 hover:text-[#D4AF37]"><Mail size={14} /></a>
                    <a href={`tel:${cleanPhone}`} title={fullPhone} className="p-2 border border-slate-200 hover:border-[#D4AF37] rounded-sm text-slate-600 hover:text-[#D4AF37]"><Phone size={14} /></a>
                    <a href={`https://wa.me/${cleanPhone}`} target="_blank" rel="noopener noreferrer" title="WhatsApp" className="p-2 border border-slate-200 hover:border-[#D4AF37] rounded-sm text-slate-600 hover:text-[#D4AF37]"><MessageCircle size={14} /></a>
                    <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`catreq-status-${r.id}`} className="text-xs uppercase tracking-[0.15em] border border-slate-300 px-3 py-2 rounded-sm">
                      <option value="new">New</option>
                      <option value="read">Read</option>
                      <option value="replied">Replied</option>
                      <option value="closed">Closed</option>
                    </select>
                    <button onClick={() => remove(r.id)} data-testid={`catreq-del-${r.id}`} className="p-2 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <F l="Email" v={r.email} />
                  <F l="Phone" v={fullPhone} />
                  <F l="Company" v={r.company} />
                  <F l="Country" v={r.country} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const F = ({ l, v }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{l}</div>
    <div className="text-[#0A1128] break-all">{v || "—"}</div>
  </div>
);

export default AdminCatalogueRequests;
